import React from 'react';
import styled from 'styled-components';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import StatusBadge from '@/components/ui-components/StatusBadge/StatusBadge';
import { ContextMenu } from '@/components/ui-components/ContextMenu/ContextMenu';
import type { ContextMenuItem } from '@/components/ui-components/ContextMenu/ContextMenu';
import { startSite, stopSite } from '@/services/siteService';

interface MonitorCardProps {
  id: string;
  name: string;
  url: string;
  status: 'UP' | 'DOWN' | 'PAUSED' ;
  protocol: string;
  uptime: string;
  lastChecked: string;
  uptimePercentage: number;
  hourlyData: number[] | null;
  onPause?: (id: string) => void;
  onStart?: (id: string) => void;
  onDelete?: (id: string) => void;
  selected?: boolean;
  onSelectedChange?: (id: string, checked: boolean) => void;
}

const Card = styled.div`
  background-color: #ffffff;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
  transition: all 0.2s ease;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    background-color: #f5f5f5;
    border-color: #d1d5db;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  /* Remove native appearance to avoid dark system background */
  -webkit-appearance: none;
  appearance: none;
  outline: none;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  display: inline-block;
  position: relative;
  transition: border-color 0.2s, background-color 0.2s;

  &:hover {
    border-color: #d1d5db;
  }

  &:checked {
    background-color: #FB6F2A;
    border-color: #FB6F2A;
  }

  &:checked::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 1px;
    width: 4px;
    height: 8px;
    border: solid #ffffff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InfoSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const MonitorName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MonitorDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #6b7280;
`;

const Protocol = styled.span`
  color: #6b7280;
  font-weight: 500;
`;

const Uptime = styled.span`
  color: #6b7280;
`;

const StatsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const LastChecked = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 14px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const UptimeBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  min-width: 200px;
`;

const UptimePercentage = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const BarContainer = styled.div`
  width: 100%;
  height: 12px;
  background-color: #f3f4f6;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  border: 1px solid #e5e7eb;
`;

const BarSegment = styled.div<{ $color: string }>`
  flex: 1;
  background-color: ${props => props.$color};
  margin: 0 1px;
  
  &:first-child {
    margin-left: 0;
  }
  
  &:last-child {
    margin-right: 0;
  }
`;

const OptionsButtonStyled = styled.div`
  color: #9ca3af;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f3f4f6;
    color: #374151;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ClickableArea = styled(Link)`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 24px;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
  }
`;

const MonitorCard: React.FC<MonitorCardProps> = ({
  id,
  name,
  status,
  protocol,
  uptime,
  lastChecked,
  uptimePercentage,
  hourlyData,
  onPause,
  onStart,
  onDelete,
  selected,
  onSelectedChange,
}) => {
  const router = useRouter();

  // Generate bar segments from API hourly_data
  // 0 = down (red), 1 = up (green), 2 = paused (gray)
  // If hourly_data is null, show all paused
  const generateBarSegments = () => {
    // Normalize values: API may return numbers or strings; treat 0/"0"=down, 1/"1"=up, 2/"2"=paused
    const data = (hourlyData && hourlyData?.length > 0
      ? hourlyData
      : Array(24).fill(2)) as Array<number | string>;

    return data.map((raw, index) => {
      const value = typeof raw === 'string' ? Number(raw) : raw;
      const color = value === 1 ? '#10b981' : value === 0 ? '#ef4444' : '#9ca3af';
      return <BarSegment key={index} $color={color} />;
    });
  };

  // Menu items based on monitor status
  const handleStart = async () => {
    try {
      await startSite(Number(id));
      onStart?.(id);
    } catch (e) {
      console.error('Failed to start site', e);
    }
  };

  const handlePause = async () => {
    try {
      await stopSite(Number(id));
      onPause?.(id);
    } catch (e) {
      console.error('Failed to stop site', e);
    }
  };

  const handleDelete = () => {
    onDelete?.(id);
  };

  const menuItems: ContextMenuItem[] = [
    // Conditional: Pause or Start monitor only
    status === 'PAUSED'
      ? {
          label: 'Start monitor',
          icon: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          onClick: handleStart,
        }
      : {
          label: 'Pause monitor',
          icon: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          onClick: handlePause,
        },
    {
      label: 'Delete monitor',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      ),
      variant: 'danger' as const,
      onClick: handleDelete,
    },
  ];

  return (
    <Card>
      <Checkbox
        type="checkbox"
        checked={!!selected}
        onChange={(e) => {
          e.stopPropagation();
          onSelectedChange?.(id, e.target.checked);
        }}
        onClick={(e) => e.stopPropagation()}
      />
      
      <StatusContainer>
        <StatusBadge status={status} size="md" />
      </StatusContainer>

      <ClickableArea href={`/monitors/${id}`}>
        <InfoSection>
          <MonitorName>{name}</MonitorName>
          <MonitorDetails>
            <Protocol>{protocol}</Protocol>
            <Uptime>{uptime}</Uptime>
          </MonitorDetails>
        </InfoSection>

        <StatsSection>
          <LastChecked>
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {lastChecked}
          </LastChecked>

          <UptimeBar>
            <UptimePercentage>{uptimePercentage}%</UptimePercentage>
            <BarContainer>
              {generateBarSegments()}
            </BarContainer>
          </UptimeBar>
        </StatsSection>
      </ClickableArea>

      <ContextMenu
        items={menuItems}
        align="right"
        trigger={
          <OptionsButtonStyled 
            data-context-menu
            onClick={(e) => e.stopPropagation()}
          >
            <svg fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </OptionsButtonStyled>
        }
      />
    </Card>
  );
};

export default MonitorCard;

